"use client";

import { useEffect, useRef, useState } from "react";

const symptoms = [
  {
    icon: "⚠️",
    title: "Réponses incohérentes",
    desc: "Chaque collaborateur répond différemment aux voyageurs sur les mêmes questions.",
  },
  {
    icon: "📉",
    title: "Avis 4 étoiles récurrents",
    desc: "Les avis 4★ remplacent peu à peu les 5★ sans raison claire identifiable.",
  },
  {
    icon: "🔄",
    title: "Problèmes qui se répètent",
    desc: "Les mêmes incidents reviennent d\'un logement à l\'autre sans solution systémique.",
  },
  {
    icon: "😰",
    title: "Charge mentale explosive",
    desc: "Vous êtes le point de passage obligé pour toutes les décisions, même mineures.",
  },
  {
    icon: "📋",
    title: "Onboarding chaotique",
    desc: "Intégrer un nouveau prestataire ou collaborateur prend des semaines d\'improvisation.",
  },
  {
    icon: "📊",
    title: "Aucune visibilité globale",
    desc: "Vous ne savez pas, en temps réel, quel logement performe et lequel pose problème.",
  },
];

export default function PlafondSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="plafond"
      ref={sectionRef}
      className="py-28 md:py-40 px-6 md:px-16 relative overflow-hidden"
      style={{ background: "var(--charcoal)" }}
    >
      {/* Giant background number */}
      <div className="absolute top-0 right-0 font-serif italic select-none pointer-events-none"
        style={{ fontSize: "clamp(8rem,20vw,18rem)", color: "rgba(255,255,255,0.03)", lineHeight: 1 }}>
        03
      </div>
      <div className="max-w-8xl mx-auto relative z-10">
        {/* Header */}
        <div
          className={`flex items-center gap-6 mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <span className="font-serif italic text-sm" style={{ color: "var(--gold)" }}>
            Page 03
          </span>
          <div className="gold-line" />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.4)" }}>
            Le plafond invisible
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-20">
          <div className="lg:col-span-6">
            <h2
              className={`font-serif text-white leading-tight mb-8 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.8rem)" }}
            >
              Au-delà de 10 logements,{" "}
              <span className="italic" style={{ color: "var(--gold-light)" }}>
                l'absence de structure devient un risque.
              </span>
            </h2>
            <p
              className={`text-base leading-relaxed transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ color: "rgba(255,255,255,0.6)" }}
            >
              Il existe un seuil critique dans la gestion locative. En dessous de 10 logements, l'humain compense les manques de process. Au-delà, le système doit prendre le relais — ou tout s'effondre progressivement.
            </p>
          </div>

          {/* Risk meter */}
          <div
            className={`lg:col-span-5 lg:col-start-8 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          >
            <div
              className="rounded-2xl p-8"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <h3 className="text-xs font-bold uppercase tracking-widest mb-8" style={{ color: "var(--gold)" }}>
                Niveau de risque par nombre de logements
              </h3>
              {[
                { range: "1–5 logements", risk: 20, label: "Faible", color: "#6B7A5E" },
                { range: "5–10 logements", risk: 50, label: "Modéré", color: "#C9A84C" },
                { range: "10–20 logements", risk: 80, label: "Élevé", color: "#D97706" },
                { range: "20+ logements", risk: 95, label: "Critique", color: "#DC2626" },
              ]?.map((item, i) => (
                <div key={i} className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>
                      {item?.range}
                    </span>
                    <span className="text-xs font-bold" style={{ color: item?.color }}>
                      {item?.label}
                    </span>
                  </div>
                  <div
                    className="h-2 rounded-full"
                    style={{ background: "rgba(255,255,255,0.08)" }}
                  >
                    <div
                      className="h-2 rounded-full transition-all duration-1000"
                      style={{
                        width: visible ? `${item?.risk}%` : "0%",
                        background: item?.color,
                        transitionDelay: `${0.4 + i * 0.15}s`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Symptoms grid */}
        <div
          className={`mb-16 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h3
            className="text-sm font-bold uppercase tracking-widest mb-10"
            style={{ color: "rgba(255,255,255,0.4)" }}
          >
            Les 6 symptômes du plafond invisible
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {symptoms?.map((s, i) => (
              <div
                key={i}
                className={`rounded-xl p-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  transitionDelay: `${0.4 + i * 0.1}s`,
                }}
              >
                <div className="text-2xl mb-4">{s?.icon}</div>
                <h4 className="font-bold text-sm text-white mb-2">{s?.title}</h4>
                <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
                  {s?.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom quote */}
        <div
          className={`transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div
            className="rounded-2xl p-10 text-center"
            style={{ background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.2)" }}
          >
            <p
              className="font-serif italic text-2xl md:text-3xl text-white"
              style={{ maxWidth: "700px", margin: "0 auto" }}
            >
              La solution n'est pas de travailler plus fort. C'est de construire{" "}
              <span style={{ color: "var(--gold-light)" }}>4 piliers fondamentaux.</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}